import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Share, Success } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import { extractScheduleArray, mapPaymentScheduleItem } from '../../../utils/venueFormat'

const IMPORTANT_INFO = [
    'Vendor will contact you within 24 hours to finalize event details',
    'Confirm final guest count 7 days before the event',
    'Balance payment is due on event day',
    'Share special requirements with vendor via chat',
]

const BOOKING_STATUS_LABELS = {
    partially_paid: 'Partially Paid',
    paid: 'Fully Paid',
    pending: 'Payment Pending',
    pending_payment: 'Payment Pending',
}

const formatMoney = (value, currency) => {
    if (value === undefined || value === null || value === '') return null
    const amount = Number(value)
    if (Number.isNaN(amount) || amount === 0) return null
    const symbol = !currency || currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${amount.toLocaleString('en-IN')}`
}

// Confirmed live 2026-09-21: booking-confirmation grew a real per-step
// schedule — {step, title, amount (string), currency, is_paid} — plus
// top-level `paid_installments`/`total_installments` counts. No `due_text`
// or `booking_status` string on this endpoint, so those are handled as
// best-effort: due text is only shown when present, and the status label is
// derived client-side from the paid/total counts.
const buildPaymentStatus = (confirmation) => {
    if (!confirmation) return null
    const schedule = extractScheduleArray(confirmation).map(mapPaymentScheduleItem)
    const paidSteps = confirmation.paid_installments ?? schedule.filter((s) => s.isPaid).length
    const totalSteps = confirmation.total_installments ?? schedule.length
    const allPaid = totalSteps > 0 && paidSteps >= totalSteps
    const nextStep = schedule.find((step) => !step.isPaid) || null
    return {
        statusLabel: allPaid
            ? BOOKING_STATUS_LABELS.paid
            : paidSteps > 0
                ? BOOKING_STATUS_LABELS.partially_paid
                : BOOKING_STATUS_LABELS.pending,
        paidSteps,
        totalSteps,
        allPaid,
        schedule,
        nextStep: nextStep
            ? {
                title: nextStep.title || '',
                amount: formatMoney(nextStep.amount, nextStep.currency) || '—',
                dueText: nextStep.dueText || '',
            }
            : null,
    }
}

const VenueBookingSuccess = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const { state: locationState } = useLocation()
    const user = useSelector((state) => state.auth?.user)
    // Only source of truth for this we currently have: the accept/action
    // response (carried forward via navigation state) — booking-confirmation
    // never returns one. Not shown at all if the state didn't make it
    // through (e.g. a hard refresh on this page), rather than a fake id.
    const bookingId = locationState?.bookingId ?? null
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    // Confirmed live 2026-09-21: booking-confirmation alone now covers
    // everything this page needs (venue/event summary, customer, vendor,
    // fare + payment method/transaction receipt, and a real per-step
    // schedule) — confirm-booking-details brought nothing this page uses
    // that booking-confirmation doesn't already have, per Jatin's explicit
    // "don't call it if not needed" — dropped entirely.
    const [confirmation, setConfirmation] = useState(null)
    const [payingNext, setPayingNext] = useState(false)

    useEffect(() => {
        if (!id) return
        setLoading(true)
        setLoadError(false)
        service
            .get(endpoints.venues.bookingConfirmation.replace('{proposal_id}', id), {}, { silent: true })
            .then(({ data }) => {
                const body = data?.data ?? data ?? null
                if (!body) throw new Error('empty response')
                setConfirmation(body)
            })
            .catch(() => {
                setConfirmation(null)
                setLoadError(true)
            })
            .finally(() => setLoading(false))
    }, [id])

    if (loading) {
        return (
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='hotelResultsEmpty'>Loading your booking...</p>
                </div>
            </section>
        )
    }

    if (loadError || !confirmation) {
        return (
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='hotelResultsEmpty'>We couldn&apos;t load your booking. It may still have gone through — check My Trips, or contact support.</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/venue-booking-confirm')}>Back to Proposals</button>
                </div>
            </section>
        )
    }

    const venueDetails = confirmation.event_and_venue || {}
    const venueBooking = {
        name: venueDetails.name || '',
        location: venueDetails.location || '',
    }

    const bookingSummary = [
        { label: 'Event Type:', value: venueDetails.event_type || '—' },
        { label: 'Date:', value: venueDetails.date || '—' },
        { label: 'Time Slot:', value: venueDetails.time_slot || '—' },
        { label: 'Guests:', value: String(venueDetails.guests ?? '—') },
    ]

    // Confirmed live 2026-09-21: booking-confirmation's `customer` object
    // reflects the contact details actually entered for THIS booking —
    // preferred over the generic redux profile, which falls back only if
    // the fetch hasn't resolved.
    const confirmationCustomer = confirmation.customer || {}
    const contactDetails = {
        name: confirmationCustomer.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '—',
        email: confirmationCustomer.email || user?.email || '—',
        phone: confirmationCustomer.phone || user?.phone || user?.mobile || '—',
    }

    const vendorContact = confirmation.vendor || null
    const confirmationSentTo = confirmation.confirmation_sent_to || ''

    const fareBreakupSource = confirmation.payment_details || {}
    const paymentDetails = [
        { label: 'Subtotal', value: formatMoney(fareBreakupSource.subtotal, fareBreakupSource.currency) },
        { label: 'Taxes & Fees', value: formatMoney(fareBreakupSource.taxes_and_fees, fareBreakupSource.currency) },
        { label: 'Platform Fee', value: formatMoney(fareBreakupSource.platform_fee, fareBreakupSource.currency) },
    ].filter((item) => item.value)

    const totalAmount = formatMoney(fareBreakupSource.payable_amount, fareBreakupSource.currency) || '—'

    // Empty until the backend settles these (seen live as "" right after a
    // fresh payment, populated a bit later as "Stripe"/a real transaction
    // id/date) — only shown once real.
    const paidVia = fareBreakupSource.payment_method || ''
    const transactionId = fareBreakupSource.transaction_id || ''
    const paymentDate = fareBreakupSource.payment_date || ''

    const paymentStatus = buildPaymentStatus(confirmation)
    const unpaidBalanceStep = paymentStatus?.schedule?.find((step) => step.step > 1 && !step.isPaid) || null
    const balanceDue = unpaidBalanceStep ? formatMoney(unpaidBalanceStep.amount, unpaidBalanceStep.currency) : null

    const unpaidSteps = paymentStatus?.schedule?.filter((step) => !step.isPaid) || []
    const remainingTotal = unpaidSteps.length
        ? formatMoney(unpaidSteps.reduce((sum, step) => sum + (Number(step.amount) || 0), 0), unpaidSteps[0].currency)
        : null
    // "Pay Full Amount" is only a meaningfully different choice from "Pay
    // Next Installment" when more than one installment is still unpaid.
    const canPayFull = unpaidSteps.length > 1

    const importantInfo = balanceDue
        ? [...IMPORTANT_INFO.slice(0, 2), `Balance payment of ${balanceDue} due on event day`, ...IMPORTANT_INFO.slice(3)]
        : IMPORTANT_INFO

    // Confirmed via Swagger 2026-09-22: the first installment is paid as
    // part of accept — every installment after that goes through this
    // endpoint instead (not `action`, not a repeat of the accept flow).
    // `pay_full` toggles between just the next installment and every
    // remaining one in a single charge.
    const handlePayInstallment = (payFull) => {
        if (payingNext) return
        setPayingNext(true)
        service
            .post(endpoints.venues.paymentRemainingInstallments, {
                quotation_id: Number(id),
                gateway: 'stripe',
                pay_full: payFull,
            })
            .then(({ data }) => {
                const body = data?.data ?? data ?? {}
                // Confirmed live 2026-09-22: the Stripe fields are nested
                // under `checkout`, not top-level like the accept response —
                // {steps, pay_full, amount, currency, checkout: {gateway,
                // client_secret, payment_intent_id, publishable_key,
                // booking_id, payable_amount, ...}}.
                const checkout = body.checkout || {}
                if (!checkout.client_secret) throw new Error('Payment session missing from the server response.')
                navigate(`/venue-payment/${id}`, {
                    state: {
                        bookingId: checkout.booking_id ?? bookingId,
                        clientSecret: checkout.client_secret,
                        paymentIntentId: checkout.payment_intent_id || '',
                        publishableKey: checkout.publishable_key || '',
                        gateway: checkout.gateway || 'stripe',
                        payAmount: body.amount ?? checkout.payable_amount ?? null,
                        payCurrency: body.currency || checkout.currency || 'INR',
                        nextStep: payFull ? { title: 'Remaining Balance', amount: '', dueText: '' } : (paymentStatus?.nextStep || null),
                    },
                })
            })
            .catch((error) => {
                showToast(error?.message || 'Could not start the payment. Please try again.', 'error')
            })
            .finally(() => setPayingNext(false))
    }

    return (
        <section className='bookingConfirm commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='row g-4'>
                    <div className='col-md-12'>
                        <div className='bookingTextConfirm'>
                            <img src={Success} alt="" />
                            <div className='bookingHead commonGredientText'>Booking Confirmed</div>
                            <p>Your venue has been successfully booked</p>
                        </div>
                    </div>
                    <div className='col-md-12'>
                        <div className='bookingConfirmInner'>
                            <div className='resultCard'>
                                {bookingId ? (
                                    <div className='bookingConfirmMeta'>
                                        <div className='bookingConfirmMetaLabel'>Booking ID <strong>#{bookingId}</strong></div>
                                    </div>
                                ) : null}
                                <div className='hotelInfoCard__top'>
                                    <div>
                                        <div className='hotelInfoCard__name'>{venueBooking.name}</div>
                                        <div className='hotelCard__dateTime'>{venueBooking.location}</div>
                                    </div>
                                </div>

                                <div className='bsFareDivider' />

                                {bookingSummary.map((item) => (
                                    <div className='bsFareRow mt-2' key={item.label}>
                                        <span>{item.label}</span>
                                        <span>{item.value}</span>
                                    </div>
                                ))}

                                <div className='bookingConfirmActions'>
                                    <button type='button' className='bookingDownloadBtn'>Download Ticket</button>
                                    <button type='button' className='bookingShareBtn' aria-label="Share">
                                        <img src={Share} alt="" />
                                    </button>
                                </div>

                                <button
                                    type='button'
                                    className='commonBtnTransparent w-100 mt-2'
                                    onClick={() => navigate('/profile', { state: { section: 'create-group' } })}
                                >
                                    Create Splitwise Group
                                </button>
                            </div>

                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Contact Details</div>
                                <div className='bsBox'>
                                    <div className='bsFareRow'>
                                        <span>Full Name:</span>
                                        <span>{contactDetails.name}</span>
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Email ID:</span>
                                        <span>{contactDetails.email}</span>
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Phone Number:</span>
                                        <span>{contactDetails.phone}</span>
                                    </div>
                                </div>
                            </div>

                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Vendor Contact</div>
                                <div className='bsBox'>
                                    {vendorContact ? (
                                        <>
                                            <div className='bsFareRow'>
                                                <span>Name:</span>
                                                <span>{vendorContact.name || '—'}</span>
                                            </div>
                                            {vendorContact.phone ? (
                                                <div className='bsFareRow'>
                                                    <span>Phone:</span>
                                                    <span>{vendorContact.phone}</span>
                                                </div>
                                            ) : null}
                                            {vendorContact.email ? (
                                                <div className='bsFareRow'>
                                                    <span>Email:</span>
                                                    <span>{vendorContact.email}</span>
                                                </div>
                                            ) : null}
                                        </>
                                    ) : (
                                        <p className='bsFareNote'>Vendor contact details will be shared once they respond.</p>
                                    )}
                                    <button
                                        type='button'
                                        className='travellerAddBtn w-100'
                                        onClick={() => navigate(`/venue-negotiate/${id}`)}
                                    >
                                        Chat With Vendor
                                    </button>
                                </div>
                            </div>

                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Confirmation Sent</div>
                                <div className='bsBox'>
                                    <p className='bookingConfirmSentText'>
                                        {confirmationSentTo
                                            ? `We've sent your booking confirmation to ${confirmationSentTo}.`
                                            : "We've sent your booking confirmation via email, SMS and WhatsApp."}
                                    </p>
                                </div>
                            </div>

                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Payment Details</div>
                                <div className='bsBox'>
                                    {paymentDetails.map((item) => (
                                        <div className='bsFareRow' key={item.label}>
                                            <span>{item.label}</span>
                                            <span>{item.value}</span>
                                        </div>
                                    ))}
                                    <div className='bsFareDivider' />
                                    <div className='bsFareRow bsFareRow--total'>
                                        <span>TOTAL AMOUNT</span>
                                        <span>{totalAmount}</span>
                                    </div>
                                    {(paidVia || transactionId || paymentDate) && <div className='bsFareDivider' />}
                                    {paidVia ? (
                                        <div className='bsFareRow'>
                                            <span>Paid Via</span>
                                            <span>{paidVia}</span>
                                        </div>
                                    ) : null}
                                    {transactionId ? (
                                        <div className='bsFareRow'>
                                            <span>Transaction ID</span>
                                            <span>{transactionId}</span>
                                        </div>
                                    ) : null}
                                    {paymentDate ? (
                                        <div className='bsFareRow'>
                                            <span>Payment Date</span>
                                            <span>{paymentDate}</span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {paymentStatus ? (
                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Status</div>
                                    <div className='bsBox'>
                                        <div className='bsFareRow'>
                                            <span>Status:</span>
                                            <span>{paymentStatus.statusLabel || '—'}</span>
                                        </div>
                                        {paymentStatus.totalSteps ? (
                                            <div className='bsFareRow'>
                                                <span>Installments Paid:</span>
                                                <span>{paymentStatus.paidSteps} of {paymentStatus.totalSteps}</span>
                                            </div>
                                        ) : null}

                                        {Array.isArray(paymentStatus.schedule) && paymentStatus.schedule.length > 0 && (
                                            <>
                                                <div className='bsFareDivider' />
                                                {paymentStatus.schedule.map((step) => (
                                                    <div className='venueScheduleRow' key={step.step}>
                                                        <div className='venueScheduleRow__label'>
                                                            <span className={`venueScheduleRow__step ${step.isPaid ? '' : 'venueScheduleRow__step--inactive'}`}>{step.step}</span>
                                                            <div>
                                                                <div className='venueScheduleRow__title'>{step.title}</div>
                                                                <div className={`venueScheduleRow__due ${step.isPaid ? 'venueScheduleRow__due--paid' : ''}`}>
                                                                    {step.isPaid
                                                                        ? `Paid${step.paymentMethodLabel ? ` via ${step.paymentMethodLabel}` : ''}`
                                                                        : step.dueText}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <span className='venueScheduleRow__value'>{formatMoney(step.amount, step.currency) || '—'}</span>
                                                    </div>
                                                ))}
                                            </>
                                        )}

                                        {!paymentStatus.allPaid && paymentStatus.nextStep ? (
                                            <>
                                                <div className='bsFareDivider' />
                                                <div className='bsFareRow'>
                                                    <span>Next Payment: {paymentStatus.nextStep.title}</span>
                                                    <span>{paymentStatus.nextStep.amount}</span>
                                                </div>
                                                {paymentStatus.nextStep.dueText ? (
                                                    <p className='bsFareNote'>{paymentStatus.nextStep.dueText}</p>
                                                ) : null}
                                                <button
                                                    type='button'
                                                    className='commonBtn w-100 mt-3'
                                                    disabled={payingNext}
                                                    onClick={() => handlePayInstallment(false)}
                                                >
                                                    {payingNext ? 'Please wait...' : 'Pay Next Installment'}
                                                </button>
                                                {canPayFull && (
                                                    <button
                                                        type='button'
                                                        className='venueModalBtn w-100 mt-2'
                                                        disabled={payingNext}
                                                        onClick={() => handlePayInstallment(true)}
                                                    >
                                                        {payingNext ? 'Please wait...' : `Pay Full Amount (${remainingTotal})`}
                                                    </button>
                                                )}
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}

                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Important Information</div>
                                <div className='bsBox'>
                                    <ul className='bsInfoList'>
                                        {importantInfo.map((info) => (
                                            <li key={info}>{info}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default VenueBookingSuccess
